class DelegatedTokenManager {
  private originalToken: string | null = null;
  private delegatedToken: string | null = null;
  private delegatedTokenExp: number | null = null;
  private audience: string | null = null;
  private clientId: string | null = null;
  private authServer: string = 'https://auth.presov.sk';
  private storageKey: string;

  constructor(audience?: string) {
    if (audience) {
      this.audience = audience;
    }
    this.storageKey = `delegated_token_manager_${audience || 'default'}`;
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.originalToken = data.originalToken;
        this.delegatedToken = data.delegatedToken;
        this.delegatedTokenExp = data.delegatedTokenExp;
        this.audience = data.audience;
        this.clientId = data.clientId;
        this.authServer = data.authServer;
      }
    } catch {
      // Ignore errors when loading from storage
    }
  }

  private saveToStorage() {
    try {
      const data = {
        originalToken: this.originalToken,
        delegatedToken: this.delegatedToken,
        delegatedTokenExp: this.delegatedTokenExp,
        audience: this.audience,
        clientId: this.clientId,
        authServer: this.authServer,
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch {
      // Ignore errors when saving to storage
    }
  }

  private parseJwtExp(token: string): number | null {
    try {
      const [, payload] = token.split('.');
      const { exp } = JSON.parse(atob(payload));
      return exp ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  setAudience(audience: string) {
    this.audience = audience;
    this.saveToStorage();
  }

  setClientId(clientId: string) {
    this.clientId = clientId;
    this.saveToStorage();
  }

  setOriginalToken(token: string) {
    this.originalToken = token;
    this.delegatedToken = null;
    this.delegatedTokenExp = null;
    this.saveToStorage();
  }

  setAuthServer(authServer: string) {
    this.authServer = authServer;
    this.saveToStorage();
  }

  async getDelegatedToken(): Promise<string> {
    if (
      this.delegatedToken &&
      this.delegatedTokenExp &&
      Date.now() < this.delegatedTokenExp - 30000
    ) {
      return this.delegatedToken;
    }
    if (!this.audience) throw new Error('Audience not set');
    if (!this.clientId) throw new Error('ClientId not set');
    if (!this.authServer) throw new Error('Auth server not set');

    const params = new URLSearchParams();
    params.append('subject_token', this.originalToken!);
    params.append(
      'grant_type',
      'urn:ietf:params:oauth:grant-type:token-exchange',
    );
    params.append(
      'subject_token_type',
      'urn:ietf:params:oauth:token-type:access_token',
    );
    params.append('audience', this.audience!);
    params.append('client_id', this.clientId!);

    const res = await fetch(`${this.authServer}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
      credentials: 'include',
    });

    if (res.status === 401) {
      throw new Error('Original token is invalid or expired.');
    }
    if (res.status === 403) {
      throw new Error('You do not have permission to exchange the token.');
    }
    if (!res.ok) throw new Error('Failed to exchange token');

    const data = await res.json();

    this.delegatedToken = data.access_token;
    // parseJwtExp očakáva string, this.delegatedToken je vždy string po priradení vyššie
    this.delegatedTokenExp =
      this.parseJwtExp(this.delegatedToken!) ||
      Date.now() + (data.expires_in - 60) * 1000;

    this.saveToStorage();
    return this.delegatedToken!;
  }

  authApiProxy<T extends { setToken(token: string): void }>(apiInstance: T): T {
    return new Proxy(apiInstance, {
      get: (target, propKey, receiver) => {
        const orig = Reflect.get(target, propKey, receiver);
        if (typeof orig !== 'function') return orig;
        return async (...args: unknown[]) => {
          if (this.originalToken) {
            const token = await this.getDelegatedToken();
            target.setToken(token);
          }
          return orig.apply(target, args);
        };
      },
    });
  }
}

export default DelegatedTokenManager;
